import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { streamAssistantChat } from '../api/assistant'

// Диалог живёт в памяти приложения, а не на странице: переход по ссылке из
// карточки на дашборд и обратно не должен стирать разговор. Перезагрузка стирает
// намеренно — ценностью считается сохранённый отчёт, а не транскрипт (ADR-0008).
const AssistantSessionContext = createContext(null)

const emptyAssistantMessage = () => ({
  role: 'assistant',
  content: '',
  cards: [],
  savedCards: [],
  error: null,
})

function cardSummaries(message) {
  const cards = message.cards?.length ? message.cards : (message.card ? [message.card] : [])
  return cards
    .filter((card) => card?.counts && typeof card.counts === 'object')
    .slice(0, 4)
    .map((card) => ({
      counts: card.counts,
      ...(typeof card.capturedAt === 'string' ? { capturedAt: card.capturedAt } : {}),
    }))
}

export const AssistantSessionProvider = ({ children }) => {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const messagesRef = useRef(messages)
  const abortRef = useRef(null)
  const streamingRef = useRef(false)

  const commit = useCallback((next) => {
    messagesRef.current = next
    setMessages(next)
  }, [])

  const patchLast = useCallback((patch) => {
    const list = messagesRef.current
    if (list.length === 0) return
    const last = list[list.length - 1]
    commit([...list.slice(0, -1), { ...last, ...patch(last) }])
  }, [commit])

  const send = useCallback(async (text) => {
    const content = (text ?? '').trim()
    if (!content || streamingRef.current) return

    const history = [...messagesRef.current, { role: 'user', content }]
    commit([...history, emptyAssistantMessage()])
    setInput('')
    streamingRef.current = true
    setStreaming(true)

    const controller = new AbortController()
    abortRef.current = controller

    try {
      await streamAssistantChat({
        messages: history.map((m) => ({
          role: m.role,
          content: m.content || '',
          ...(m.role === 'assistant' ? { cards: cardSummaries(m) } : {}),
        })),
        signal: controller.signal,
        onEvent: ({ event, data }) => {
          if (event === 'text' && data?.delta) {
            patchLast((last) => ({ content: `${last.content || ''}${data.delta}` }))
          } else if (event === 'card' && data) {
            patchLast((last) => ({ cards: [...(last.cards || []), data] }))
          } else if (event === 'error' && data?.message) {
            patchLast(() => ({ error: data.message }))
          }
        },
      })
    } catch (err) {
      if (err.name !== 'AbortError') {
        patchLast(() => ({ error: err.code === 'unconfigured' ? 'unconfigured' : err.message || 'error' }))
      }
    } finally {
      streamingRef.current = false
      setStreaming(false)
      abortRef.current = null
    }
  }, [commit, patchLast])

  const stop = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  // Сброс молчаливый: транскрипт не артефакт, терять в нём нечего.
  const reset = useCallback(() => {
    abortRef.current?.abort()
    commit([])
    setInput('')
  }, [commit])

  // Повтор после обрыва: снимаем оборванный ответ и заново шлём тот же вопрос.
  const retry = useCallback(() => {
    const list = messagesRef.current
    const lastUser = [...list].reverse().find((m) => m.role === 'user')
    if (!lastUser) return
    const lastIsFailedAssistant = list[list.length - 1]?.role === 'assistant'
    commit(lastIsFailedAssistant ? list.slice(0, -2) : list.slice(0, -1))
    send(lastUser.content)
  }, [commit, send])

  const markSaved = useCallback((index, cardIndex = 0) => {
    const list = messagesRef.current
    commit(list.map((msg, i) => {
      if (i !== index) return msg
      const savedCards = Array.isArray(msg.savedCards) ? [...msg.savedCards] : []
      savedCards[cardIndex] = true
      return { ...msg, savedCards }
    }))
  }, [commit])

  useEffect(() => () => abortRef.current?.abort(), [])

  const value = {
    messages,
    input,
    setInput,
    streaming,
    send,
    stop,
    reset,
    retry,
    markSaved,
  }

  return (
    <AssistantSessionContext.Provider value={value}>
      {children}
    </AssistantSessionContext.Provider>
  )
}

export const useAssistantSession = () => {
  const context = useContext(AssistantSessionContext)
  if (!context) {
    throw new Error('useAssistantSession требует AssistantSessionProvider')
  }
  return context
}

export default AssistantSessionContext
